import { ColumnConfig, RouteEnum } from "layout/TableConstructor/types";
import { Competency_Map_Status, CompetencyMapsQuery, PlatformCompetencyMapListOrderFields } from "@generated/graphql";
import { gradeColor } from "helpers/index";
import { Chip, Stack, Typography } from "@mui/material";
import { COMPETENCY_MAP_STATUS_TITLE } from "constants/global";
import dayjs from "dayjs";
import { CompetencyMapFilters } from "pages/CompetencyMaps/list/tableConfig/filterFields";
import ActionButtonWithPopoper from "../components/ActionButtonWithPopoper";
import { RefetchCompetencyMaps } from "../types";

export type CompetencyMapItem = NonNullable<CompetencyMapsQuery["competencyMaps"]["data"]>[number];

const getColumnConfig = (
  refetch: RefetchCompetencyMaps
): ColumnConfig<
  CompetencyMapItem,
  CompetencyMapFilters,
  PlatformCompetencyMapListOrderFields,
  undefined,
  RouteEnum.CompetencyMap
>[] => [
  {
    sortField: PlatformCompetencyMapListOrderFields.NumericId,
    title: "ID",
    render: (data) => data.numericId,
    filterName: "numericId",
    name: "numericId",
  },
  {
    sortField: PlatformCompetencyMapListOrderFields.Title,
    title: "Название",
    render: (data) => (
      <Stack
        spacing={1}
        direction='row'
        alignItems='center'
        useFlexGap>
        {data.status === Competency_Map_Status.Archive && (
          <Chip
            label='Архив'
            sx={{ backgroundColor: "background.default", color: "base.500" }}
          />
        )}
        <Typography variant='body14rg'>{data.title}</Typography>
      </Stack>
    ),
    filterName: "title",
    name: "title",
  },
  {
    sortField: PlatformCompetencyMapListOrderFields.Specialization,
    title: "Специализация",
    render: (data) => data.specialization || "-",
    filterName: "specialization",
    name: "specialization",
  },
  {
    name: "grades",
    title: "Грейд",
    filterName: "grades",
    render: (data) =>
      data.grades ? (
        <Stack
          spacing={0.5}
          direction='row'>
          {data.grades?.map((grade) => (
            <Chip
              key={`grade-${grade}`}
              label={grade}
              sx={{ backgroundColor: `${gradeColor(grade)}.light`, color: `${gradeColor(grade)}.contrast` }}
            />
          ))}
        </Stack>
      ) : (
        "-"
      ),
  },
  {
    name: "competencesCount",
    title: "Количество компетенций",
    render: (data) => data.competencesCount || 0,
    filterName: "competencesCount",
  },
  {
    name: "trialsCount",
    title: "Количество тестирований",
    render: (data) => data.trialsInfo.count || 0,
    filterName: "trialsCount",
  },
  {
    sortField: PlatformCompetencyMapListOrderFields.CreatedAt,
    title: "Дата создания",
    render: (data) => (data.createdAt ? dayjs(data.createdAt).format("DD.MM.YYYY") : "-"),
    filterName: "createdAt",
    name: "createdAt",
  },
  {
    sortField: PlatformCompetencyMapListOrderFields.UpdatedAt,
    title: "Дата изменения",
    render: (data) => (data.updatedAt ? dayjs(data.updatedAt).format("DD.MM.YYYY") : "-"),
    filterName: "updatedAt",
    name: "updatedAt",
  },
  {
    name: "lastTrialDate",
    title: "Дата последнего тестирования",
    render: (data) => (data.lastTrialDate ? dayjs(data.lastTrialDate).format("DD.MM.YYYY") : "-"),
    filterName: "lastTrialAt",
  },
  {
    title: "Оценка репозитория",
    sortField: PlatformCompetencyMapListOrderFields.IsRepositoryCheck,
    render: (data) => (data.isRepositoryCheck ? "Да" : "Нет"),
    filterName: "isRepositoryCheck",
    name: "isRepositoryCheck",
  },
  {
    sortField: PlatformCompetencyMapListOrderFields.Status,
    title: "Статус",
    render: (data) => (data.status ? COMPETENCY_MAP_STATUS_TITLE[data.status] : "-"),
    filterName: "status",
    name: "status",
  },
  {
    name: "action",
    title: "Действие",
    render: (data) => {
      return (
        <ActionButtonWithPopoper
          competencyMap={data}
          refetch={refetch}
        />
      );
    },
  },
];

export default getColumnConfig;
